import {
  IsoV1Country,
  IxcRegistrationV1AccountReviewType,
  IxcRegistrationV1CipOfac,
  IxcRegistrationV1CipVendor,
  IxcRegistrationV1ClearingControlsSettingsCipProgram,
  IxcRegistrationV1ControlsMarginType,
  IxcRegistrationV1RegistrationAccountType,
  type IxcRegistrationV1UsState,
  IxmUserauthV1BankAccountValidation,
} from '@interxfi/types/generated/api';
import type { CreateProgramFormSteps } from '../create-program-drawer';
import type { OptionalProgramContactGroupFormValues } from './contacts/optional-program-contact-group-form';
import type { ProgramContactGroupFormValues } from './contacts/program-contact-group-form';
import type { ProgramRegistrationTypeFormValues } from './registration-type/program-registration-type-form';

export function abbreviateProgramName(name: string): string {
  const words = name.split(/[^a-zA-Z]+/).filter(Boolean);

  let abbreviation = '';

  for (const word of words) {
    const uppercasedWord = word.toUpperCase();
    if (['INC', 'LLC', 'CO', 'LLP'].includes(uppercasedWord)) {
      continue;
    }

    if (abbreviation.length < 4) {
      abbreviation += uppercasedWord[0];
    }
  }

  if (abbreviation.length < 4) {
    for (const word of words) {
      let i = 1;
      while (abbreviation.length < 4 && i < word.length) {
        abbreviation += word[i].toUpperCase();
        i += 1;
      }
    }
  }

  return abbreviation.padEnd(4, 'X').slice(0, 4);
}

type FormStepsWithoutMetaIds = Omit<CreateProgramFormSteps, 'details'> & {
  details: Omit<CreateProgramFormSteps['details'], 'organizationId' | 'realmId'>;
};

export async function generateSeedProgramData(): Promise<FormStepsWithoutMetaIds> {
  const faker = (await import('@faker-js/faker/locale/en_US')).faker;

  function getCrdNumber(): string {
    return `${faker.number
      .int({ min: 0, max: 999999 })
      .toString()
      .padEnd(faker.number.int({ min: 2, max: 6 }), '0')}`;
  }

  function getSecRegistrationNumber(): string {
    return `${faker.number
      .int({ min: 0, max: 999 })
      .toString()
      .padEnd(faker.number.int({ min: 1, max: 3 }), '0')}-${faker.number
      .int({ min: 0, max: 999_999 })
      .toString()
      .padEnd(faker.number.int({ min: 5, max: 5 }), '0')}`;
  }

  function getRegistrationType(): ProgramRegistrationTypeFormValues {
    return faker.helpers.arrayElement([
      {
        kind: 'brokerDealer',
        value: {
          crdNumber: getCrdNumber(),
          secRegistrationNumber: getSecRegistrationNumber(),
        },
      },
      {
        kind: 'investmentAdviser',
        value: {
          crdNumber: getCrdNumber(),
          secRegistrationNumber: getSecRegistrationNumber(),
        },
      },
    ]);
  }

  function getProgramContacts(): ProgramContactGroupFormValues {
    return [
      {
        name: faker.person.fullName(),
        title: faker.person.jobTitle(),
        phone: faker.phone.number({ style: 'national' }),
        email: faker.internet.email({ provider: 'blackhole.interxfi' }),
      },
    ];
  }

  function getOptionalProgramContacts(): OptionalProgramContactGroupFormValues {
    const isProviding = faker.datatype.boolean();

    return {
      isProviding,
      value: isProviding ? getProgramContacts() : [],
    };
  }

  const name = faker.company.name();
  const state = faker.location.state({ abbreviated: true }) as IxcRegistrationV1UsState;

  return {
    details: {
      details: {
        registrationType: getRegistrationType(),
        programInformation: {
          name,
        },
        correspondentInformation: {
          legalName: name,
          dba: '',
          webDomains: [{ url: faker.internet.url({ protocol: 'https' }) }],
          taxIdentifier: `${faker.number.int({ min: 0, max: 99 }).toString().padStart(2, '0')}-${faker.number.int({ min: 0, max: 9999999 }).toString().padStart(7, '0')}`,
          mainOfficeAddress: {
            type: IsoV1Country.Us,
            value: {
              street1: faker.location.streetAddress(),
              street2: faker.location.secondaryAddress(),
              city: faker.location.city(),
              state,
              postalCode: faker.location.zipCode({ state }),
            },
          },
          mailingAddress: { isProviding: false, value: undefined },
          mailingAddressIsSameAsMainOffice: false,
        },
        customerSupportContact: {
          displayName: '',
          website: faker.internet.url({ protocol: 'https' }),
          emailAddress: faker.internet.email({ provider: 'blackhole.interxfi' }),
          phone: faker.phone.number({ style: 'national' }),
          phoneTaxDocuments: faker.phone.number({ style: 'national' }),
          mailingAddress: {
            isSameAsMainOffice: true,
            value: undefined,
          },
        },
        vendors: {
          cipIdentity: faker.helpers.arrayElement(
            Object.values(IxcRegistrationV1CipVendor).filter((x) => x !== IxcRegistrationV1CipVendor.Unspecified),
          ),
          cipOfac: faker.helpers.arrayElement(Object.values(IxcRegistrationV1CipOfac).filter((x) => x !== IxcRegistrationV1CipOfac.Unspecified)),
          bankAccountValidation: faker.helpers.arrayElement(
            Object.values(IxmUserauthV1BankAccountValidation).filter((x) => x !== IxmUserauthV1BankAccountValidation.Unspecified),
          ),
        },
      },
    },
    clearingDetails: {
      programCode: {
        kind: 'mutable',
        value: abbreviateProgramName(name),
      },
      shortName: name.toUpperCase().replace(/\s+/g, '').slice(0, 16),
      mpid: faker.datatype.boolean() ? faker.string.alpha({ length: 4, casing: 'upper' }) : '',
      taxVendorId: faker.number.int({ min: 100_000_000, max: 999_999_999 }).toString(),
      proxyVendorId: faker.number.int({ min: 100_000_000, max: 999_999_999 }).toString(),
    },
    contacts: {
      executive: getProgramContacts(),
      technical: getProgramContacts(),
      billing: getProgramContacts(),
      operations: getProgramContacts(),
      compliance: getOptionalProgramContacts(),
      disasterRecovery: getOptionalProgramContacts(),
      aml: getOptionalProgramContacts(),
      cat: getOptionalProgramContacts(),
    },
    controls: {
      apiAuth: {
        apiKeys: {
          canCreate: { value: true, apiKeysMax: '1000' },
          canGetToken: true,
          canRevoke: true,
        },
      },
      books: {
        canCreditCustomers: true,
        canDebitCustomers: true,
        canTradeUnsettledCash: true,
        canCreditPlatformAccount: true,
        canDebitPlatformAccounts: true,
      },
      instantDeposit: {
        canUseAdvance: {
          value: true,
          limits: {
            max: '5000',
          },
        },
      },
      accountNumbers: {
        canCreate: true,
      },
      funding: {
        maxSourcesPerAccount: '5',
        ach: {
          canDeposit: {
            value: true,
            limits: {
              minAmount: '1',
              maxAmount: '100000',
            },
          },
          canWithdraw: {
            value: true,
            limits: {
              minAmount: '1',
              maxAmount: '25000',
            },
          },
        },
        sameDayAch: {
          canDeposit: {
            value: true,
            limits: {
              minAmount: '1',
              maxAmount: '100000',
            },
          },
          canWithdraw: {
            value: true,
            limits: {
              minAmount: '1',
              maxAmount: '25000',
            },
          },
        },
        wire: {
          canWithdraw: {
            value: true,
            limits: {
              minAmount: '1',
              maxAmount: '50000',
            },
          },
        },
        batch: {
          ach: {
            canDeposit: true,
            canWithdraw: true,
          },
          sameDayAch: {
            canDeposit: true,
            canWithdraw: true,
          },
          deposit: {
            max: '250000',
            autoApproveUnder: '25000',
          },
          withdrawal: {
            max: '100000',
            autoApproveUnder: '10000',
          },
        },
      },
      orders: {
        usEquities: {
          canPlaceClosing: true,
          canPlaceOpening: true,
          canPlaceFractional: true,
          canChargeCommission: true,
          // notionalHoldBufferPercent: (faker.helpers.rangeToNumber({ min: 0, max: 100 }) / 100).toString(),
        },
      },
      batchOrder: {
        canPlaceBatchOrders: true,
      },
      registration: {
        cipProgram: IxcRegistrationV1ClearingControlsSettingsCipProgram.Disclosed,
        accountReviewType: IxcRegistrationV1AccountReviewType.ClearingAutomated,
        specialPurposeAccountReviewType: IxcRegistrationV1AccountReviewType.ClearingManual,
        accounts: {
          canCreate: {
            value: true,
            types: Object.values(IxcRegistrationV1RegistrationAccountType).filter((x) => x !== IxcRegistrationV1RegistrationAccountType.Unspecified),
          },
          marginType: IxcRegistrationV1ControlsMarginType.Lpm,
        },
        states: faker.helpers.arrayElement([{ kind: 'all' }, { kind: 'allStatesPlusDc' }]),
        optionalSuitabilityQuestions: true,
      },
      sweeps: {
        canSweepAccounts: {
          value: true,
          defaultSweepPlanId: '',
          eligibleSweepPlanIds: [],
        },
      },
    },
  };
}
